import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField } from '@mui/material';
import { FormEvent, useEffect, useState } from 'react';

interface NewVersionDialogProps {
  open: boolean;
  /** The name as last loaded or saved, not the draft's: a rename is part of what is being confirmed. */
  savedName: string;
  version: number | null;
  /** Who the template is available to, e.g. "every company" or "7 companies". */
  audience: string;
  saving: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * A template in use changes its content only as a new version, which cannot be taken back from here,
 * while name, status and companies go to every version. So the save waits for the name typed out.
 */
const NewVersionDialog = ({ open, savedName, version, audience, saving, onCancel, onConfirm }: NewVersionDialogProps) => {
  const [typed, setTyped] = useState('');
  useEffect(() => {
    if (open) setTyped('');
  }, [open]);
  const next = version === null ? 'a new version' : `version ${version + 1}`;
  const confirmed = typed.trim() === savedName.trim() && savedName.trim() !== '';
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (confirmed && !saving) onConfirm();
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onCancel} maxWidth="sm" fullWidth PaperProps={{ sx: { borderTop: 6, borderColor: 'error.main' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
        <WarningAmberIcon color="error" />
        This template is in use
      </DialogTitle>
      <DialogContent>
        <Alert severity="error" variant="filled" sx={{ mb: 2 }}>
          Saving creates {next}. It cannot be undone here: a version is never deleted and its plugins never come off.
        </Alert>
        <DialogContentText sx={{ mb: 2 }}>
          Pay policies use a version of this template, so its plugins, their settings and its description cannot change in place. Those changes go into {next}: pay policies keep the version they use, and the ones created from now on get {next}.
        </DialogContentText>
        <DialogContentText sx={{ mb: 2 }}>
          The name, status and companies are not versioned. They apply to every version of this template, including the ones pay policies use, so every version will be available to {audience}. If you only want to try something, start a new template from this one instead.
        </DialogContentText>
        <Box component="form" onSubmit={submit}>
          <TextField label="Type the current template name to confirm" fullWidth value={typed} onChange={(event) => setTyped(event.target.value)} helperText={savedName} color="error" autoComplete="off" />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={saving} variant="outlined" autoFocus>
          Cancel
        </Button>
        <Button variant="contained" color="error" onClick={onConfirm} disabled={!confirmed || saving}>
          {saving ? <CircularProgress size={20} color="inherit" /> : `Create ${next}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewVersionDialog;
